/**
 * The dashboard's own navigation, and the rule for when it applies.
 *
 * The dashboard is the only screen in the product with a sidebar. Every other
 * page keeps the top tab bar, at every width — a deliberate choice rather
 * than an unfinished migration, and the reason is that a rail takes width
 * from the content column: a 320px rail on a 1366px laptop leaves 1046px
 * where the tables expect 1152px, so Interview Candidates (33 columns) and
 * the Joining Tracker (18) would scroll sideways sooner on every screen.
 * Confining it to the one page that has no wide table keeps that cost at
 * zero everywhere else.
 *
 * Two navigation models in one product is the price, and it is paid
 * knowingly: the dashboard is a different kind of page, and the rail is what
 * makes it read as one.
 *
 * **Nothing here re-implements a permission.** `navFor` returns the bar with
 * every role rule already applied — staffOnly, ownerOnly, adminOnly,
 * supervisorOnly, hideFromOwner, and empty groups dropped. This only reshapes
 * what that function returns into the sections a rail draws. Its docstring
 * gives the reason the filtering lives there and not at the call site: "a
 * test that reimplemented the filtering would pass while the bar was wrong."
 */
import { LifeBuoy } from 'lucide-react';
import { navFor } from './nav';
import type { NavGroup, NavItem } from './nav';
import type { User } from '../types';

/**
 * The routes that get the rail.
 *
 * Two of them now. A recruiter's day and an administrator's are different
 * screens answering different questions, and `/` no longer renders either —
 * it dispatches by role to whichever of these the caller belongs on. Both are
 * rail pages for the same reason: neither carries a wide table, so neither
 * pays the 224px the rail costs.
 *
 * Exact matches, never a prefix. This began as `pathname === '/'` with a note
 * that `startsWith` would put a sidebar on every page in the product, since
 * `/` prefixes all of them. That hazard is gone with `/` itself, but the rule
 * stays exact: `/admin/home` is a prefix of nothing today and there is no
 * reason to leave a trap for the day it is.
 */
export const DASHBOARD_PATHS = ['/my-day', '/admin/home'] as const;

/** Kept as the recruiter's landing, which is what most callers mean by it. */
export const DASHBOARD_PATH = DASHBOARD_PATHS[0];

/** The administrator's landing. */
export const ADMIN_HOME_PATH = DASHBOARD_PATHS[1];

export function isDashboardRoute(pathname: string): boolean {
  return (DASHBOARD_PATHS as readonly string[]).includes(pathname);
}

/**
 * Help, which the rail carries and the top bar does not.
 *
 * The one link here that `navFor` does not supply, and the only place the
 * rail departs from the bar. It is deliberate rather than an oversight: it
 * is not recruitment work, it belongs to no group, and putting it in
 * `nav.ts` would add a tab to all twenty-odd pages that are meant to look
 * exactly as they did.
 *
 * The cost is real and worth stating: somebody stuck on the Joining Tracker
 * has to come back to the dashboard to reach it. That is one click, and the
 * dashboard is home.
 *
 * No role flag. Every role has something to read, and the page itself gates
 * its chapters -- an employee is shown the four that apply to them rather
 * than being refused the screen.
 */
export const HELP_ITEM: NavItem = {
  to: '/help',
  label: 'Help',
  icon: LifeBuoy,
};

/**
 * A titled block of links in the rail.
 *
 * `collapsible` separates the two kinds. The top-level items are the day's
 * work and are always open; the groups are accordions, because a company
 * admin's bar flattens to 28 links and a rail showing all of them at once is
 * a scroll, not a navigation.
 */
export interface RailSection {
  /** The heading above the block. Null for the first, which needs none. */
  label: string | null;
  items: NavItem[];
  collapsible: boolean;
}

/**
 * The rail's sections for one user.
 *
 * The top-level links first under a "Work" heading, mirroring the reference,
 * then one accordion per group in the order `navFor` returns them.
 *
 * A group marked `asTab` — none at present, since Job Postings went — is a
 * destination that the top bar renders as a plain link rather than a menu
 * that opens to reveal one thing. A rail has no such distinction: one item
 * under a heading looks the same either way, so it is emitted as a
 * non-collapsible section and reads as the link it is.
 *
 * `viewing` repeats the two rules AppLayout applies to the bar while somebody
 * is looking at another person's screens: the grouped navigation goes
 * entirely, because that is where Admin lives, and any remaining /admin link
 * is dropped. Not an extra permission — the server refuses those routes under
 * a view-as token either way, so what this prevents is a rail offering a set
 * of doors that all answer 403. It has to be repeated rather than inherited
 * because `navFor` takes a user and impersonation is not part of one.
 */
export function railSections(
  user: User | null,
  { viewing = false }: { viewing?: boolean } = {},
): RailSection[] {
  const nav = navFor(user);
  const topLevel = viewing
    ? nav.topLevel.filter((item) => !item.to.startsWith('/admin'))
    : nav.topLevel;
  const groups = viewing ? [] : nav.groups;
  const sections: RailSection[] = [];

  if (topLevel.length > 0) {
    sections.push({ label: 'Work', items: topLevel, collapsible: false });
  }

  for (const group of groups as NavGroup[]) {
    sections.push({
      label: group.label,
      items: group.items,
      // A single-destination group is a link wearing a heading. Collapsing it
      // would hide one item behind a click that reveals one item.
      collapsible: !group.asTab && group.items.length > 1,
    });
  }

  // Last, and headingless. Help is not one of the day's jobs and does not
  // belong under a group name; a heading over a single link would only be
  // the word Help written twice.
  sections.push({ label: null, items: [HELP_ITEM], collapsible: false });

  return sections;
}

/**
 * Which accordions start open.
 *
 * The one holding the current page, and nothing else. Opening everything
 * defeats the accordion; opening nothing means somebody on /payslips sees no
 * sign of where they are.
 *
 * Matched the way the top bar's dropdowns match — exact, or a path beneath —
 * so a detail route under an item still lights its own section.
 */
export function sectionHoldingPath(
  sections: RailSection[],
  pathname: string,
): string | null {
  for (const section of sections) {
    if (!section.collapsible || !section.label) continue;
    const holds = section.items.some(
      (item) => pathname === item.to || pathname.startsWith(`${item.to}/`),
    );
    if (holds) return section.label;
  }
  return null;
}
